/**
 * LoadingSpinner
 *
 * Branded spinner component using the WritersLogic gradient.
 * Accessible, responsive to prefers-reduced-motion, and available
 * in three sizes for different contexts.
 */

import { memo } from 'react';

export interface LoadingSpinnerProps {
  /** Spinner diameter: sm=16px, md=32px, lg=48px */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Accessible label for screen readers (defaults to "Loading") */
  label?: string;
}

const SIZE_CONFIG = {
  sm: { px: 16, border: 2 },
  md: { px: 32, border: 3 },
  lg: { px: 48, border: 4 },
} as const;

/**
 * Branded loading spinner with gradient stroke.
 *
 * Uses an SVG with a conic gradient via a rotating arc so the
 * brand gradient (#D4A843 -> #DDB85E) is visible on the spinner.
 * Falls back to a static indicator when prefers-reduced-motion
 * is enabled.
 */
export const LoadingSpinner = memo(function LoadingSpinner({
  size = 'md',
  className = '',
  label = 'Loading',
}: LoadingSpinnerProps) {
  const { px, border } = SIZE_CONFIG[size];
  const radius = (px - border) / 2;
  const circumference = 2 * Math.PI * radius;
  // Show ~75% of the circle as the arc
  const arcLength = circumference * 0.75;
  const gapLength = circumference - arcLength;

  return (
    <span
      className={`inline-flex items-center justify-center ${className}`}
      role="status"
    >
      <svg
        width={px}
        height={px}
        viewBox={`0 0 ${px} ${px}`}
        fill="none"
        className="loading-spinner-svg"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={`spinner-gradient-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--gradient-primary-start, #D4A843)" />
            <stop offset="100%" stopColor="var(--gradient-primary-end, #DDB85E)" />
          </linearGradient>
        </defs>
        {/* Background track */}
        <circle
          cx={px / 2}
          cy={px / 2}
          r={radius}
          stroke="rgba(255, 255, 255, 0.1)"
          strokeWidth={border}
        />
        {/* Gradient arc */}
        <circle
          cx={px / 2}
          cy={px / 2}
          r={radius}
          stroke={`url(#spinner-gradient-${size})`}
          strokeWidth={border}
          strokeLinecap="round"
          strokeDasharray={`${arcLength} ${gapLength}`}
          transform={`rotate(-90 ${px / 2} ${px / 2})`}
        />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  );
});

export default LoadingSpinner;
